<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Lista informacji</h4>
        </template>
        <template v-else>
          <h4>Lista informacji pacjenta:
            <p class="mt-1 mb-0 patient-name">{{ patient.name + ' ' + patient.last_name }}</p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddInformationPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj informacje
        </d-button>
      </d-col>
    </d-row>
    <d-card
      class="w-100"
      style="box-shadow: none;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Data wystawienia</th>
            <th scope="col" class="border-0">Data wizyty</th>
            <th scope="col" class="border-0">Specjalista</th>
            <th scope="col" class="border-0">Rodzaj diagnozy</th>
            <th scope="col" class="border-0 text-center">Plik diagnozy</th>
            <th scope="col" class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(information, informationIndex) of informationList"
            :key="'information-' + informationIndex">
            <td class="align-middle">
              {{information.created_at.slice(0, 10)}}
            </td>
            <td class="align-middle">
              <template v-if="information.visit_date !== 'brak...'">
                <p style="color: #17c671">{{ information.visit_date.slice(0, 11) }}</p>
                <span style="margin-top: -30px; display: block; font-size: 0.9rem; opacity: 0.6">
                  {{ information.visit_date.slice(11, 16) }} - {{ information.visit_date.slice(11, 16) }}
                </span>
              </template>
              <template v-else>
                <span style="opacity: 0.5">Brak</span>
              </template>
            </td>
            <td class="align-middle">
              {{ getUserName(information.user_id) }}
            </td>
            <td class="align-middle">
              <template v-if="information.diagnosis_name !== 'brak...'">
                {{information.diagnosis_name}}
              </template>
              <template v-else>
                <span style="opacity: 0.5">Brak</span>
              </template>
            </td>
            <td class="align-middle text-center">
              <d-button
                theme="info"
                outline
                :disabled="actionInProgress !== false"
                @click.prevent="downloadInformation(information.id)">
                <template v-if="actionInProgress !== information.id">
                  <i class="material-icons">cloud_download</i>
                  <span v-if="!isInPopup" class="ml-2">Pobierz</span>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </td>
            <td
              class="align-middle text-center"
              style="width: 130px;">
              <d-button
                theme="primary"
                @click.prevent="showEditInformationPopup(information.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeInformation(information.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="informationList.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak informacji do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <patients-form-information-popup
      v-if="isShowInfoPopup"
      :on-close-popup="handleCloseInformationPopup"
      :on-submit-callback="loadItemsData"
      :patient-id="patient.id"
      :information-id="editInfoID || 0"
      :show="true"/>

  </div>
</template>

<script>
import Vue from 'vue';
import ListUtils from '@/utils/ListUtils.js';
import PatientsFormInformationPopup from '@/components/forms/patients/PatientsFormInformationPopup.vue';

export default {
  name: 'patients-form-information',
  props: {
    allUsers: {
      type: Array,
      required: true
    },
    isInPopup: {
      type: Boolean,
      default: false
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    patient: {
      type: Object,
      required: true
    }
  },
  components: {
    'patients-form-information-popup': PatientsFormInformationPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      editInfoID: 0,
      informationList: [],
      isShowInfoPopup: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);

      ListUtils.loadItemsData(this, {
        endpoint: '/patients/diagnosis-information/items?patient_id=' + this.patient.id,
        listField: 'informationList',
        pagination: true
      });
    },
    removeInformation (informationID) {
      Vue.set(this, 'removeInProgress', true);

      ListUtils.removeItem(this, {
        endpointBase: '/patients/diagnosis-information/delete/',
        id: informationID,
        successTitle: 'Usunięto informacje',
        successText: 'Wybrana informacja został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tą informację?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie informacji nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadItemsData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadInformation (informationID) {
      this.actionInProgress = true;

      this.$http.post('/patients/diagnosis-information/get-file/' + informationID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = 'Informacja-nr-';
        link.setAttribute('download', filename + informationID + '.pdf');
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        document.body.removeChild(link);
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.actionInProgress = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    getUserName (userID) {
      if (!userID) {
        return 'brak';
      }

      let itemFound = this.allUsers.find(item => item.id === userID);

      if (!itemFound) {
        return 'Nie znaleziono specjalisty';
      }

      return itemFound.name + ' ' + itemFound.last_name;
    },
    showAddInformationPopup () {
      Vue.set(this, 'editInfoID', 0);
      Vue.set(this, 'isShowInfoPopup', true);
    },
    showEditInformationPopup (informationID) {
      Vue.set(this, 'editInfoID', informationID);
      Vue.set(this, 'isShowInfoPopup', true);
    },
    handleCloseInformationPopup () {
      Vue.set(this, 'isShowInfoPopup', false);
      Vue.set(this, 'editInfoID', 0);
    }
  }
}
</script>
